<template>
  <div class="m-1">
    <strong>{{ $t('type') }}</strong>
    <Dropdown
      :options="typeOptions"
      :value="options.type"
      class="mb-3"
      @change="
        propChanged('type', $event)
        propChanged('href', '')
      "
    />

    <Input
      v-if="options.type === 'link'"
      :label="$t('href')"
      :value="options.href"
      class="mb-3"
      @enter="propChanged('href', $event)"
      @blur="propChanged('href', $event)"
    />

    <template v-else-if="options.type === 'download'">
      <input ref="fileRef" type="file" accept="application/pdf" @change="handleFile" hidden />
      <template v-if="options.filename && options.href">
        <strong>{{ $t('upload-file.current-file') }}</strong>
        <a
          :href="options.href"
          :download="options.filename"
          target="_blank"
          class="d-block text-dark font-weight-bold mb-0"
          >{{ options.filename }}</a
        >
      </template>
      <Button
        icon="fas fa-upload"
        :label="$t('upload-file.button', { size: $options.filters.prettyBytes($t('upload-file.max-size')) })"
        class="mb-3"
        @click="$refs.fileRef.click()"
      />
    </template>

    <strong>{{ $t('target') }}</strong>
    <Dropdown :options="targetOptions" :value="options.target" @change="propChanged('target', $event)" class="mb-3" />

    <Input
      :label="$t('content')"
      :value="options.text.content"
      class="mb-3"
      @enter="propChanged('text.content', $event)"
      @blur="propChanged('text.content', $event)"
    />

    <strong>{{ $t('font-size') }} (px)</strong>
    <PlacementNumber :initial="options.text.size" @change="propChanged('text.size', $event)" class="mb-3" />

    <hr />
    <div class="advanced-collapsible__item">
      <FontStyleButton text="text" :values="options.text" @change="propChanged($event.path, $event.value)" />
    </div>
    <hr />

    <strong>{{ $t('text-color') }}</strong>
    <div class="d-flex justify-content-around mb-3">
      <ColorPicker
        :label="$t('normal')"
        :color="options.text.default"
        @submit="propChanged('text.default', $event)"
        class="mb-3"
      />
      <ColorPicker
        :label="$t('hover')"
        :color="options.text.hover"
        open-direction="left"
        @submit="propChanged('text.hover', $event)"
      />
    </div>

    <strong>{{ $t('background-color') }}</strong>
    <div class="d-flex justify-content-around mb-3">
      <ColorPicker
        :label="$t('normal')"
        :color="options.background.default"
        @submit="propChanged('background.default', $event)"
      />
      <ColorPicker
        :label="$t('hover')"
        :color="options.background.hover"
        open-direction="left"
        @submit="propChanged('background.hover', $event)"
      />
    </div>

    <Border :border="options.border" @change="propChanged(`border.${$event.field}`, $event.value)" class="mb-3" />

    <Spacement
      :label="$t('padding')"
      :options="options.padding"
      class="mb-3"
      @change="propChanged(`padding.${$event.field}`, $event.value)"
    />
  </div>
</template>

<script>
import WidgetSideNavMixin from './Mixin.js'
import UserMixin from '@/components/Mixin.js'
import FontStyleMixin from '@/mixins/FontStyleMixin'

import FontStyleButton from '@/shared/components/FontStyleButton'
import { infoAlert, successAlert, errorAlert } from '@/helpers/alertHelper.js'

import UploadFileService from '@/services/UploadFile.js'

export default {
  name: 'ButtonSideNav',
  i18nOptions: { keyPrefix: 'components.sidenav.landing.panel.widget.button-side-nav' },
  mixins: [WidgetSideNavMixin, UserMixin, FontStyleMixin],
  components: { FontStyleButton },
  computed: {
    targetOptions() {
      return [
        { value: '_blank', text: this.$i18n.t('components.sidenav.landing.panel.widget.button-side-nav.open-new-tab') },
        { value: '', text: this.$i18n.t('components.sidenav.landing.panel.widget.button-side-nav.open-same-tab') },
      ]
    },
    typeOptions() {
      const options = [
        { value: 'link', text: this.$t('types.link') },
        {
          value: 'download',
          text: this.$t('types.download'),
        },
      ]

      return this.isFromFunnel
        ? [
            {
              value: 'next-page',
              text: this.$t('types.next-page'),
            },
            ...options,
          ]
        : options
    },
  },
  methods: {
    handleFile(event) {
      this.isPdfValid(event.target.files[0])
        .then(() => {
          this.options.loading = true
          infoAlert(this.$t('upload-file.upload-init'))
          UploadFileService.uploadCore(event.target.files[0])
            .then(response => {
              this.propChanged('href', response.data.data.uploadAsset.url)
              this.propChanged('filename', response.data.data.uploadAsset.name)
              successAlert(this.$t('upload-file.success'))
            })
            .catch(() => {
              errorAlert(this.$t('upload-file.error'))
            })
            .finally(() => (this.options.loading = false))
        })
        .catch(msg => errorAlert(msg))
    },
    isPdfValid(file) {
      return new Promise((resolve, reject) => {
        if (file.size > this.$t('upload-file.max-size')) {
          reject(this.$t('upload-file.error-size'))
        } else if (!file.type.match(/pdf/)) {
          reject(this.$t('upload-file.error-type'))
        } else {
          resolve()
        }
      })
    },
  },
}
</script>
